import { useEffect, useState, useCallback } from "react";
import {
  ScrollText, ShieldAlert, LogIn, KeyRound, Trash2, Lock, ChevronDown, Loader2, AlertTriangle,
} from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EVENT_TYPES = [
  "login_success", "login_failed", "logout", "password_changed", "forced_password_change",
  "password_reset", "signout_all", "user_created", "user_role_changed", "user_permissions_changed",
  "user_activated", "user_deactivated", "record_created", "record_edited", "record_soft_deleted",
  "record_restored", "record_permanently_deleted",
];
const AREAS = ["Authentication", "Administration", "Command Centre", "Product", "Launch", "Marketing", "Investors", "Finance", "Decisions & Risks"];
const ROLES = ["administrator", "editor", "viewer"];
const OUTCOMES = ["success", "failure"];

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const ALL = "__all__";

const eventTone = (e) =>
  e === "login_failed" || e?.includes("permanently") || e === "record_soft_deleted" || e === "user_deactivated"
    ? "bg-[#DC2626]/10 text-[#B91C1C] border-[#DC2626]/25"
    : e === "login_success" || e === "record_restored" || e === "user_activated"
      ? "bg-[#0EA5A4]/10 text-[#0F766E] border-[#0EA5A4]/25"
      : e?.includes("password") || e === "user_role_changed" || e === "user_permissions_changed" || e === "signout_all"
        ? "bg-[#E0A82E]/12 text-[#A16207] border-[#E0A82E]/35"
        : "bg-[#EEF2FB] text-[#5A6B8C] border-[#DCE3F2]";

const StatCard = ({ icon: Icon, label, value, tone = "text-[#14245F]", testid }) => (
  <div data-testid={testid} className="rounded-xl border border-[#E4E9F3] bg-white p-4">
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#5A6B8C]">
      <Icon size={14} /> {label}
    </div>
    <p className={`mt-2 text-2xl font-semibold ${tone}`}>{value}</p>
  </div>
);

function SecurityWidget({ s }) {
  if (!s) return null;
  return (
    <div data-testid="security-widget" className="rounded-xl border border-[#E4E9F3] bg-white p-5">
      <div className="mb-4 flex items-center gap-2">
        <ShieldAlert size={16} className="text-[#1D4ED8]" />
        <h3 className="font-heading text-base font-semibold text-[#14245F]">Security Overview</h3>
      </div>
      {s.warnings?.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="security-warnings">
          {s.warnings.map((w) => (
            <div key={w} className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <AlertTriangle size={15} /> {w}
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#5A6B8C]">Recent logins</p>
          <ul className="space-y-1 text-sm text-[#14245F]">
            {(s.recent_logins || []).slice(0, 5).map((l) => (
              <li key={l.id} className="flex justify-between gap-2">
                <span className="truncate">{l.user_name || l.user_email}</span>
                <span className="shrink-0 text-xs text-[#5A6B8C]">{fmt(l.created_at)}</span>
              </li>
            ))}
            {(s.recent_logins || []).length === 0 && <li className="text-sm text-[#5A6B8C]">None yet</li>}
          </ul>
        </div>
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#5A6B8C]">Locked accounts</p>
          {(s.locked_accounts || []).length === 0 ? (
            <p className="text-sm text-[#5A6B8C]">No active lockouts</p>
          ) : (
            <ul className="space-y-1 text-sm text-red-700">
              {s.locked_accounts.map((l) => (
                <li key={l.identifier} className="flex items-center gap-1.5"><Lock size={12} /> {l.identifier}</li>
              ))}
            </ul>
          )}
          <p className="mt-3 mb-1 text-xs font-semibold uppercase tracking-wider text-[#5A6B8C]">Recent password events</p>
          <ul className="space-y-1 text-sm text-[#14245F]">
            {(s.recent_resets || []).slice(0, 3).map((r) => (
              <li key={r.id} className="truncate">{r.action} · {r.record_name || r.user_email}</li>
            ))}
            {(s.recent_resets || []).length === 0 && <li className="text-sm text-[#5A6B8C]">None recently</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Row({ log }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr data-testid={`audit-row-${log.id}`} className="cursor-pointer border-b border-[#EEF2FB] hover:bg-[#F5F8FF]" onClick={() => setOpen(!open)}>
        <td className="px-4 py-3 text-xs text-[#5A6B8C]">{fmt(log.created_at)}</td>
        <td className="px-4 py-3">
          <Badge variant="outline" className={eventTone(log.event_type)}>{log.event_type}</Badge>
        </td>
        <td className="px-4 py-3 text-sm text-[#14245F]">{log.action}</td>
        <td className="px-4 py-3 text-sm">{log.user_name || log.user_email || <span className="italic text-[#5A6B8C]">anonymous</span>}</td>
        <td className="px-4 py-3 text-xs text-[#5A6B8C]">{log.area || "—"}</td>
        <td className="px-4 py-3 text-xs">{log.record_name || "—"}</td>
        <td className="px-4 py-3 text-right"><ChevronDown size={15} className={`inline transition-transform ${open ? "rotate-180" : ""}`} /></td>
      </tr>
      {open && (
        <tr data-testid={`audit-detail-${log.id}`} className="border-b border-[#EEF2FB] bg-[#F9FBFF]">
          <td colSpan={7} className="px-6 py-3 text-xs text-[#5A6B8C]">
            <div className="grid grid-cols-2 gap-x-8 gap-y-1 md:grid-cols-3">
              <span><b>Outcome:</b> {log.outcome}</span>
              <span><b>Role:</b> {log.role || "—"}</span>
              <span><b>Email:</b> {log.user_email || "—"}</span>
              <span><b>Record type:</b> {log.record_type || "—"}</span>
              <span><b>Record ID:</b> {log.record_id || "—"}</span>
              <span><b>IP:</b> {log.ip_address || "—"}</span>
              {log.previous_values && <span className="col-span-3"><b>Previous:</b> {JSON.stringify(log.previous_values)}</span>}
              {log.new_values && <span className="col-span-3"><b>New:</b> {JSON.stringify(log.new_values)}</span>}
              {log.context && <span className="col-span-3"><b>Context:</b> {JSON.stringify(log.context)}</span>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AuditLog() {
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 50 });
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ search: "", event_type: ALL, area: ALL, role: ALL, outcome: ALL, date_from: "", date_to: "", page: 1 });

  const load = useCallback(async () => {
    setLoading(true);
    const params = { page: f.page, page_size: 50 };
    ["search", "event_type", "area", "role", "outcome", "date_from", "date_to"].forEach((k) => {
      if (f[k] && f[k] !== ALL) params[k] = f[k];
    });
    try {
      const res = await api.auditLogs(params);
      setData(res);
    } finally {
      setLoading(false);
    }
  }, [f]);

  useEffect(() => { const t = setTimeout(() => load(), 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.auditSummary().then(setSummary).catch(() => {}); }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v, page: k === "page" ? v : 1 }));
  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));

  return (
    <>
      <PageHeader eyebrow="Administration" title="Audit Log" description="A complete, append-only record of security events and record changes. Newest first." />

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <StatCard testid="stat-events-today" icon={ScrollText} label="Events today" value={summary?.events_today ?? "—"} />
        <StatCard testid="stat-logins-today" icon={LogIn} label="Logins today" value={summary?.logins_today ?? "—"} tone="text-teal-700" />
        <StatCard testid="stat-failed-24h" icon={ShieldAlert} label="Failed 24h" value={summary?.failed_logins_24h ?? "—"} tone="text-red-700" />
        <StatCard testid="stat-password-events" icon={KeyRound} label="Password events" value={summary?.password_events_24h ?? "—"} />
        <StatCard testid="stat-destructive" icon={Trash2} label="Destructive 24h" value={summary?.destructive_24h ?? "—"} tone="text-red-700" />
        <StatCard testid="stat-lockouts" icon={Lock} label="Lockouts" value={summary?.active_lockouts ?? "—"} tone="text-amber-700" />
      </div>

      <div className="mb-6"><SecurityWidget s={summary} /></div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input data-testid="audit-search" placeholder="Search action, user, record…" value={f.search} onChange={(e) => set("search", e.target.value)} className="h-9 w-full max-w-xs" />
        <FilterSelect testid="filter-event-type" value={f.event_type} onChange={(v) => set("event_type", v)} placeholder="Event" options={EVENT_TYPES} />
        <FilterSelect testid="filter-area" value={f.area} onChange={(v) => set("area", v)} placeholder="Area" options={AREAS} />
        <FilterSelect testid="filter-role" value={f.role} onChange={(v) => set("role", v)} placeholder="Role" options={ROLES} />
        <FilterSelect testid="filter-outcome" value={f.outcome} onChange={(v) => set("outcome", v)} placeholder="Outcome" options={OUTCOMES} />
        <Input type="date" data-testid="filter-date-from" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} className="h-9 w-[150px]" />
        <Input type="date" data-testid="filter-date-to" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} className="h-9 w-[150px]" />
      </div>

      <div data-testid="audit-table" className="overflow-hidden rounded-xl border border-[#E4E9F3] bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-[#E4E9F3] bg-[#F5F8FF] text-left text-xs uppercase tracking-wider text-[#5A6B8C]">
                <th className="px-4 py-3 font-medium">Time</th>
                <th className="px-4 py-3 font-medium">Event</th>
                <th className="px-4 py-3 font-medium">Action</th>
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Area</th>
                <th className="px-4 py-3 font-medium">Record</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="py-12 text-center"><Loader2 className="inline h-5 w-5 animate-spin text-[#1D4ED8]" /></td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={7} className="py-12 text-center text-[#5A6B8C]">No events match your filters.</td></tr>
              ) : (
                data.items.map((log) => <Row key={log.id} log={log} />)
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-[#5A6B8C]">
        <span>{data.total} events</span>
        <div className="flex items-center gap-2">
          <Button data-testid="audit-prev" size="sm" variant="outline" disabled={f.page <= 1} onClick={() => set("page", f.page - 1)}>Previous</Button>
          <span>Page {f.page} / {totalPages}</span>
          <Button data-testid="audit-next" size="sm" variant="outline" disabled={f.page >= totalPages} onClick={() => set("page", f.page + 1)}>Next</Button>
        </div>
      </div>
    </>
  );
}

const FilterSelect = ({ testid, value, onChange, placeholder, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="h-9 w-[150px]"><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="__all__">All {placeholder.toLowerCase()}s</SelectItem>
      {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);
